import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ContentCopy as CopyIcon,
  Code as CodeIcon,
  Preview as PreviewIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  InsertDriveFile as TemplateIcon,
  Check as CheckIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  ColorLens as ColorIcon,
} from '@mui/icons-material';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

interface EmailTemplate {
  id: string;
  name: string;
  description: string;
  subject: string;
  html_content: string;
  text_content: string;
  category: string;
  template_variables: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TEMPLATE_CATEGORIES = [
  { value: 'retention', label: 'Retenção' },
  { value: 'promotion', label: 'Promoção' },
  { value: 'welcome', label: 'Boas-vindas' },
  { value: 'reactivation', label: 'Reativação' },
  { value: 'vip', label: 'VIP' },
  { value: 'birthday', label: 'Aniversário' },
  { value: 'other', label: 'Outro' },
];

const AVAILABLE_VARIABLES = [
  { var: '{{player_name}}', description: 'Nome do jogador' },
  { var: '{{email}}', description: 'Email do jogador' },
  { var: '{{bonus_amount}}', description: 'Valor do bônus' },
  { var: '{{login_url}}', description: 'URL de login' },
  { var: '{{unsubscribe_url}}', description: 'URL para cancelar inscrição' },
  { var: '{{offer_title}}', description: 'Título da oferta' },
  { var: '{{offer_description}}', description: 'Descrição da oferta' },
  { var: '{{offer_url}}', description: 'URL da oferta' },
  { var: '{{expiry_date}}', description: 'Data de expiração' },
  { var: '{{birthday_bonus}}', description: 'Bônus de aniversário' },
  { var: '{{free_spins}}', description: 'Rodadas grátis' },
  { var: '{{redeem_url}}', description: 'URL para resgatar' },
  { var: '{{vip_level}}', description: 'Nível VIP' },
  { var: '{{balance}}', description: 'Saldo atual' },
];

export default function EmailTemplates() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [editorTab, setEditorTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [useTextOnly, setUseTextOnly] = useState(false); // Novo estado para controlar modo texto simples

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    subject: '',
    html_content: '',
    text_content: '',
    category: 'retention',
    is_active: true,
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/email/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (template?: EmailTemplate) => {
    if (template) {
      setEditingTemplate(template);
      // Verificar se o template usa texto simples (tem text_content mas não html_content ou html vazio)
      const hasHtml = !!(template.html_content && template.html_content.trim() !== '');
      const hasText = !!(template.text_content && template.text_content.trim() !== '');
      const isTextOnly = hasText && !hasHtml;
      setUseTextOnly(!!isTextOnly);
      setEditorTab(isTextOnly ? 1 : 0);
      setFormData({
        name: template.name,
        description: template.description,
        subject: template.subject,
        html_content: template.html_content || '',
        text_content: template.text_content || '',
        category: template.category,
        is_active: template.is_active,
      });
    } else {
      setEditingTemplate(null);
      setUseTextOnly(false);
      setEditorTab(0);
      setFormData({
        name: '',
        description: '',
        subject: '',
        html_content: getDefaultHtmlTemplate(),
        text_content: '',
        category: 'retention',
        is_active: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTemplate(null);
    setEditorTab(0);
    setUseTextOnly(false);
  };

  const handleToggleTextOnly = (checked: boolean) => {
    if (checked) {
      // Ativar texto simples: limpar HTML e mudar para aba de texto
      setFormData({ ...formData, html_content: '' });
      setEditorTab(1);
    } else {
      // Ativar HTML: limpar texto simples e mudar para aba de HTML
      setFormData({ ...formData, text_content: '' });
      setEditorTab(0);
      // Se não tiver HTML, adicionar template padrão
      if (!formData.html_content || formData.html_content.trim() === '') {
        setFormData({ ...formData, html_content: getDefaultHtmlTemplate(), text_content: '' });
      }
    }
    setUseTextOnly(checked);
  };

  const handleSaveTemplate = async () => {
    try {
      if (editingTemplate) {
        await axios.put(`${getApiUrl()}/email/templates/${editingTemplate.id}`, formData);
      } else {
        await axios.post(`${getApiUrl()}/email/templates`, formData);
      }
      loadTemplates();
      handleCloseDialog();
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      alert('Erro ao salvar template');
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este template?')) {
      try {
        await axios.delete(`${getApiUrl()}/email/templates/${id}`);
        loadTemplates();
      } catch (error) {
        console.error('Erro ao excluir template:', error);
        alert('Erro ao excluir template');
      }
    }
  };

  const handleDuplicateTemplate = async (template: EmailTemplate) => {
    try {
      await axios.post(`${getApiUrl()}/email/templates`, {
        ...template,
        name: `${template.name} (Cópia)`,
        id: undefined,
      });
      loadTemplates();
    } catch (error) {
      console.error('Erro ao duplicar template:', error);
    }
  };

  const handlePreview = (html: string) => {
    // Substituir variáveis com valores de exemplo
    let preview = html;
    preview = preview.replace(/{{player_name}}/g, 'João Silva');
    preview = preview.replace(/{{email}}/g, 'joao@exemplo.com');
    preview = preview.replace(/{{bonus_amount}}/g, 'R$ 100,00');
    preview = preview.replace(/{{login_url}}/g, 'https://bwino.nubomind.com/login');
    preview = preview.replace(/{{unsubscribe_url}}/g, '#cancelar-inscricao');
    preview = preview.replace(/{{offer_title}}/g, 'Oferta Especial');
    preview = preview.replace(/{{offer_description}}/g, 'Aproveite esta oferta exclusiva!');
    preview = preview.replace(/{{offer_url}}/g, 'https://bwino.nubomind.com/ofertas');
    preview = preview.replace(/{{expiry_date}}/g, '31/12/2025');
    preview = preview.replace(/{{birthday_bonus}}/g, 'R$ 50,00');
    preview = preview.replace(/{{free_spins}}/g, '20');
    preview = preview.replace(/{{redeem_url}}/g, 'https://bwino.nubomind.com/resgatar');
    preview = preview.replace(/{{vip_level}}/g, 'Gold');
    preview = preview.replace(/{{balance}}/g, 'R$ 500,00');
    
    setPreviewHtml(preview);
    setOpenPreview(true);
  };

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('html-content') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.html_content;
      const newText = text.substring(0, start) + variable + text.substring(end);
      setFormData({ ...formData, html_content: newText });
      
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }
  };

  const getDefaultHtmlTemplate = () => {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body {
      font-family: Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      margin: 0;
      padding: 0;
      background-color: #f5f5f5;
    }
    .container {
      max-width: 600px;
      margin: 20px auto;
      background: white;
      border-radius: 10px;
      overflow: hidden;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    }
    .header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 40px 30px;
      text-align: center;
    }
    .header h1 {
      margin: 0;
      font-size: 28px;
    }
    .content {
      padding: 40px 30px;
    }
    .content h2 {
      color: #667eea;
      margin-top: 0;
    }
    .cta-button {
      display: inline-block;
      background: #667eea;
      color: white;
      padding: 15px 40px;
      text-decoration: none;
      border-radius: 5px;
      font-weight: bold;
      margin: 20px 0;
    }
    .cta-button:hover {
      background: #764ba2;
    }
    .footer {
      background: #333;
      color: white;
      padding: 20px;
      text-align: center;
      font-size: 12px;
    }
    .footer a {
      color: #aaa;
      text-decoration: none;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>🎰 Bwino Casino</h1>
    </div>
    <div class="content">
      <h2>Olá {{player_name}}!</h2>
      <p>Seu conteúdo aqui...</p>
      
      <center>
        <a href="{{login_url}}" class="cta-button">ACESSAR AGORA</a>
      </center>
      
      <p>Atenciosamente,<br>Equipe Bwino</p>
    </div>
    <div class="footer">
      <p>&copy; 2025 Bwino Casino. Todos os direitos reservados.</p>
      <p><a href="{{unsubscribe_url}}">Cancelar inscrição</a></p>
    </div>
  </div>
</body>
</html>`;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box className="glass-blue-card rounded-xl p-4 mb-6" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ color: '#000000', mb: 1 }}>
            📝 Templates de Email
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Crie e gerencie templates personalizados para suas campanhas
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
            },
          }}
        >
          Novo Template
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className="glass-blue-card">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Total de Templates
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#000000' }}>{templates.length}</Typography>
                </Box>
                <TemplateIcon sx={{ fontSize: 40, color: '#3b82f6', opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className="glass-blue-card">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Ativos
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#000000' }}>
                    {templates.filter((t) => t.is_active).length}
                  </Typography>
                </Box>
                <CheckIcon sx={{ fontSize: 40, color: '#10b981', opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className="glass-blue-card">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Categorias
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#000000' }}>
                    {new Set(templates.map((t) => t.category)).size}
                  </Typography>
                </Box>
                <ColorIcon sx={{ fontSize: 40, color: '#f59e0b', opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className="glass-blue-card">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Variáveis Disponíveis
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#000000' }}>{AVAILABLE_VARIABLES.length}</Typography>
                </Box>
                <CodeIcon sx={{ fontSize: 40, color: '#3b82f6', opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Templates Table */}
      <TableContainer component={Paper} className="glass-blue-card">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: 'rgba(59, 130, 246, 0.08)', color: '#000000', fontWeight: 600 }}>Nome</TableCell>
              <TableCell sx={{ backgroundColor: 'rgba(59, 130, 246, 0.08)', color: '#000000', fontWeight: 600 }}>Categoria</TableCell>
              <TableCell sx={{ backgroundColor: 'rgba(59, 130, 246, 0.08)', color: '#000000', fontWeight: 600 }}>Assunto</TableCell>
              <TableCell align="center" sx={{ backgroundColor: 'rgba(59, 130, 246, 0.08)', color: '#000000', fontWeight: 600 }}>Status</TableCell>
              <TableCell align="center" sx={{ backgroundColor: 'rgba(59, 130, 246, 0.08)', color: '#000000', fontWeight: 600 }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((template) => (
              <TableRow key={template.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: '#000000' }}>
                    {template.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    {template.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={
                      TEMPLATE_CATEGORIES.find((c) => c.value === template.category)?.label ||
                      template.category
                    }
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell sx={{ color: '#475569' }}>{template.subject}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={template.is_active ? 'Ativo' : 'Inativo'}
                    color={template.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="Visualizar">
                      <IconButton
                        size="small"
                        onClick={() => handlePreview(template.html_content)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDialog(template)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicar">
                      <IconButton
                        size="small"
                        onClick={() => handleDuplicateTemplate(template)}
                      >
                        <CopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteTemplate(template.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {templates.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                  <TemplateIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Nenhum template criado ainda
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Crie seu primeiro template de email
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog Editor */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth
        PaperProps={{
          sx: {
            background: '#ffffff',
            border: '2px solid #3b82f6',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(59, 130, 246, 0.2)',
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#ffffff', 
          backgroundColor: '#3b82f6',
          borderBottom: '2px solid #2563eb',
          fontWeight: 600,
          fontSize: '1.25rem',
          py: 2
        }}>
          {editingTemplate ? '✏️ Editar Template' : '➕ Novo Template'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Nome do Template"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Descrição"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Assunto do Email"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
                helperText="Pode usar variáveis como {{player_name}}"
                sx={{ mb: 2 }}
              />

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Categoria</InputLabel>
                    <Select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      label="Categoria"
                    >
                      {TEMPLATE_CATEGORIES.map((cat) => (
                        <MenuItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={(e) =>
                          setFormData({ ...formData, is_active: e.target.checked })
                        }
                      />
                    }
                    label="Template Ativo"
                  />
                </Grid>
              </Grid>

              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useTextOnly}
                      onChange={(e) => handleToggleTextOnly(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body1" fontWeight="bold">
                      {useTextOnly ? '📝 Modo Texto Simples' : '🎨 Modo HTML'}
                    </Typography>
                  }
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', mt: 0.5 }}>
                  {useTextOnly 
                    ? 'Template será enviado apenas em texto simples (HTML desabilitado)'
                    : 'Template será enviado em HTML (texto simples opcional como fallback)'}
                </Typography>
              </Box>

              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs 
                  value={editorTab} 
                  onChange={(_, v) => {
                    setEditorTab(v);
                    // Se mudar para HTML, desativar texto simples
                    if (v === 0 && useTextOnly) {
                      handleToggleTextOnly(false);
                    }
                    // Se mudar para texto simples, ativar modo texto
                    if (v === 1 && !useTextOnly) {
                      handleToggleTextOnly(true);
                    }
                  }}
                >
                  <Tab 
                    label="HTML" 
                    icon={<CodeIcon />} 
                    iconPosition="start"
                    disabled={useTextOnly}
                  />
                  <Tab 
                    label="Texto Simples" 
                    icon={<TemplateIcon />} 
                    iconPosition="start"
                    disabled={!useTextOnly}
                  />
                </Tabs>
              </Box>

              <TabPanel value={editorTab} index={0}>
                {!useTextOnly ? (
                  <>
                    <TextField
                      id="html-content"
                      fullWidth
                      multiline
                      rows={16}
                      value={formData.html_content}
                      onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                      placeholder="Cole seu HTML aqui ou use o template padrão..."
                      sx={{
                        fontFamily: 'monospace',
                        '& textarea': {
                          fontFamily: 'monospace',
                          fontSize: '13px',
                        },
                      }}
                    />
                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<PreviewIcon />}
                        onClick={() => handlePreview(formData.html_content)}
                      >
                        Visualizar
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Alert severity="info">
                    O modo Texto Simples está ativo. HTML está desabilitado. Use a aba "Texto Simples" para editar.
                  </Alert>
                )}
              </TabPanel>

              <TabPanel value={editorTab} index={1}>
                {useTextOnly ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={16}
                    value={formData.text_content}
                    onChange={(e) => setFormData({ ...formData, text_content: e.target.value })}
                    placeholder="Digite o conteúdo do email em texto simples..."
                    required
                    sx={{
                      '& textarea': {
                        fontFamily: 'monospace',
                        fontSize: '13px',
                      },
                    }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    multiline
                    rows={16}
                    value={formData.text_content}
                    onChange={(e) => setFormData({ ...formData, text_content: e.target.value })}
                    placeholder="Versão em texto simples (opcional, usado como fallback para clientes que não suportam HTML)..."
                    sx={{
                      '& textarea': {
                        fontFamily: 'monospace',
                        fontSize: '13px',
                      },
                    }}
                  />
                )}
              </TabPanel>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ 
                position: 'sticky', 
                top: 20,
                background: '#ffffff',
                border: '2px solid #3b82f6',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)',
              }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: '#000000', mb: 1, fontWeight: 600 }}>
                    📌 Variáveis Disponíveis
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                    Clique para inserir no template
                  </Typography>
                  <List dense>
                    {AVAILABLE_VARIABLES.map((v) => (
                      <ListItem
                        key={v.var}
                        button
                        onClick={() => insertVariable(v.var)}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        <ListItemIcon>
                          <CodeIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={<code style={{ fontSize: '12px' }}>{v.var}</code>}
                          secondary={v.description}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '2px solid #3b82f6', 
          backgroundColor: '#f8fafc',
          p: 2 
        }}>
          <Button 
            onClick={handleCloseDialog} 
            startIcon={<CloseIcon />}
            sx={{
              color: '#64748b',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#e2e8f0',
                color: '#475569',
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveTemplate}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={
              !formData.name || 
              !formData.subject || 
              (!useTextOnly && !formData.html_content) ||
              (useTextOnly && !formData.text_content)
            }
            sx={{
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#2563eb',
              },
              '&:disabled': {
                backgroundColor: '#cbd5e1',
                color: '#94a3b8',
              }
            }}
          >
            Salvar Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={openPreview} onClose={() => setOpenPreview(false)} maxWidth="md" fullWidth
        PaperProps={{
          sx: {
            background: '#ffffff',
            border: '2px solid #3b82f6',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(59, 130, 246, 0.2)',
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#ffffff', 
          backgroundColor: '#3b82f6',
          borderBottom: '2px solid #2563eb',
          fontWeight: 600,
          fontSize: '1.25rem',
          py: 2
        }}>👁️ Preview do Email</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Este é um preview com dados de exemplo. As variáveis serão substituídas pelos dados reais ao enviar.
          </Alert>
          <Box
            sx={{
              border: '1px solid #ddd',
              borderRadius: 1,
              overflow: 'hidden',
              minHeight: '400px',
            }}
          >
            <iframe
              srcDoc={previewHtml}
              style={{
                width: '100%',
                minHeight: '500px',
                border: 'none',
              }}
              title="Email Preview"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '2px solid #3b82f6', 
          backgroundColor: '#f8fafc',
          p: 2 
        }}>
          <Button 
            onClick={() => setOpenPreview(false)}
            sx={{
              color: '#64748b',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#e2e8f0',
                color: '#475569',
              }
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}







